import React, { useEffect, useMemo, useState } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { PositionExperienceRow } from '../types';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
    buildExperienceInsight,
    getPositionOptions,
    getPreferredPositions,
    shortExperience,
    sortExperienceRows,
} from '../utils/dataTransforms';
import {
    formatCompactSalary,
    formatSalary,
    formatSample,
} from '../utils/formatters';
import AccessibleDataTable from './AccessibleDataTable';
import ChartTooltip from './ChartTooltip';
import SelectField from './SelectField';

interface ExperienceGrowthProps {
    rows: PositionExperienceRow[];
    selectedYear: number;
}

const ExperienceTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) {
        return null;
    }

    const row = payload[0].payload as PositionExperienceRow;

    return (
        <ChartTooltip
            title={row.position}
            rows={[
                { label: 'Deneyim', value: row.experience },
                {
                    label: 'Medyan maaş',
                    value: formatSalary(row.medianSalary),
                },
                {
                    label: 'Örneklem',
                    value: formatSample(row.sampleCount),
                },
            ]}
        />
    );
};

const ExperienceGrowth: React.FC<ExperienceGrowthProps> = ({
    rows,
    selectedYear,
}) => {
    const yearRows = useMemo(
        () => rows.filter((row) => row.year === selectedYear),
        [rows, selectedYear]
    );
    const options = useMemo(() => getPositionOptions(yearRows), [yearRows]);
    const preferred = useMemo(
        () => getPreferredPositions(yearRows),
        [yearRows]
    );
    const [position, setPosition] = useState(preferred[0] ?? '');
    const mobile = useMediaQuery('(max-width: 680px)');
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    useEffect(() => {
        if (!options.includes(position)) {
            setPosition(preferred[0] ?? options[0] ?? '');
        }
    }, [options, position, preferred]);

    const chartRows = useMemo(
        () =>
            sortExperienceRows(
                yearRows.filter((row) => row.position === position)
            ),
        [position, yearRows]
    );
    const insight = useMemo(
        () => buildExperienceInsight(chartRows, position),
        [chartRows, position]
    );

    return (
        <>
            <div className="section-control">
                <SelectField
                    id="experience-position"
                    label="Pozisyon"
                    value={position}
                    onChange={(event) => setPosition(event.target.value)}
                >
                    {options.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </SelectField>
            </div>

            <div className="insight-card insight-card--top">
                <span aria-hidden="true">↗</span>
                <p>{insight}</p>
            </div>

            <div
                className="chart-panel chart-panel--line"
                role="img"
                aria-label={`${position} için deneyime göre medyan maaş gelişimi`}
            >
                <div className="chart-canvas" aria-hidden="true">
                    <ResponsiveContainer width="100%" height={430}>
                        <LineChart
                            data={chartRows}
                            margin={{
                                top: 24,
                                right: mobile ? 8 : 24,
                                bottom: 12,
                                left: 4,
                            }}
                        >
                            <CartesianGrid
                                vertical={false}
                                stroke="#d9d5ca"
                                strokeDasharray="2 5"
                            />
                            <XAxis
                                dataKey="experience"
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                tickFormatter={
                                    mobile
                                        ? shortExperience
                                        : (value) => value
                                }
                                tick={{
                                    fill: '#5e655f',
                                    fontSize: mobile ? 9 : 11,
                                    fontWeight: 500,
                                }}
                                dy={10}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={formatCompactSalary}
                                tick={{
                                    fill: '#737a74',
                                    fontSize: mobile ? 9 : 10,
                                    fontFamily: 'DM Mono, monospace',
                                }}
                                width={mobile ? 56 : 70}
                            />
                            <Tooltip
                                cursor={{
                                    stroke: '#a9ada8',
                                    strokeDasharray: '3 4',
                                }}
                                content={<ExperienceTooltip />}
                            />
                            <Line
                                type="monotone"
                                dataKey="medianSalary"
                                stroke="#264338"
                                strokeWidth={3}
                                dot={{
                                    r: 4,
                                    fill: '#f8f6ef',
                                    stroke: '#264338',
                                    strokeWidth: 2,
                                }}
                                activeDot={{
                                    r: 6,
                                    fill: '#264338',
                                    stroke: '#f8f6ef',
                                    strokeWidth: 3,
                                }}
                                isAnimationActive={!reduceMotion}
                                animationDuration={700}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <AccessibleDataTable
                    caption={`${position} deneyim ve maaş verileri`}
                    headers={['Deneyim', 'Medyan maaş', 'Örneklem']}
                    rows={chartRows.map((row) => [
                        row.experience,
                        formatSalary(row.medianSalary),
                        formatSample(row.sampleCount),
                    ])}
                />
            </div>
        </>
    );
};

export default React.memo(ExperienceGrowth);
